import { useRef, useState, useCallback, useEffect, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Group } from '@mantine/core'
import IonIcon from '@reacticons/ionicons'

interface CustomResizeableModalProps {
    opened: boolean
    onClose: () => void
    title?: string
    children: ReactNode | ((maximize: { onMaximize: () => void; isMaximized: boolean }) => ReactNode)
    initialWidth?: number
    initialHeight?: number
    minWidth?: number
    minHeight?: number
}

export default function CustomResizeableModal({
    opened,
    onClose,
    title,
    children,
    initialWidth = 1200,
    initialHeight = 700,
    minWidth = 800,
    minHeight = 500,
}: CustomResizeableModalProps) {

    const [modalDimensions, setModalDimensions] = useState({ width: initialWidth, height: initialHeight });
    const [isResizing, setIsResizing] = useState(false);
    const [justFinishedResizing, setJustFinishedResizing] = useState(false);
    const [resizeDirection, setResizeDirection] = useState<string>('');
    const [resizeStartPos, setResizeStartPos] = useState({ x: 0, y: 0 });
    const [resizeStartDimensions, setResizeStartDimensions] = useState({ width: 0, height: 0 });
    const [isMaximized, setIsMaximized] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);
    const finishedResizingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleResizeStart = useCallback((e: React.MouseEvent, direction: string) => {
        if (isMaximized) return;
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        setResizeDirection(direction);
        setResizeStartPos({ x: e.clientX, y: e.clientY });

        // Get the actual current dimensions
        if (modalRef.current) {
            const rect = modalRef.current.getBoundingClientRect();
            setResizeStartDimensions({ width: rect.width, height: rect.height });
        } else {
            setResizeStartDimensions(modalDimensions);
        }
    }, [modalDimensions, isMaximized]);

    const handleResizeMove = useCallback((e: MouseEvent) => {
        if (!isResizing || isMaximized) return;

        const deltaX = e.clientX - resizeStartPos.x;
        const deltaY = e.clientY - resizeStartPos.y;

        const MAX_WIDTH = window.innerWidth - 2;
        const MAX_HEIGHT = window.innerHeight - 2;

        let newWidth = resizeStartDimensions.width;
        let newHeight = resizeStartDimensions.height;

        if (resizeDirection.includes('right')) {
            newWidth = resizeStartDimensions.width + deltaX;
        }
        if (resizeDirection.includes('left')) {
            newWidth = resizeStartDimensions.width - deltaX;
        }
        if (resizeDirection.includes('bottom')) {
            newHeight = resizeStartDimensions.height + deltaY;
        }
        if (resizeDirection.includes('top')) {
            newHeight = resizeStartDimensions.height - deltaY;
        }

        newWidth = Math.max(Math.min(newWidth, MAX_WIDTH), minWidth);
        newHeight = Math.max(Math.min(newHeight, MAX_HEIGHT), minHeight);

        setModalDimensions({ width: newWidth, height: newHeight });
    }, [isResizing, resizeStartPos, resizeStartDimensions, resizeDirection, minWidth, minHeight, isMaximized]);

    const handleResizeEnd = useCallback(() => {
        if (isResizing) {
            setIsResizing(false);
            setResizeDirection('');
            setJustFinishedResizing(true);

            // cleanup body styles & remove event listeners
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleResizeMove);
            document.removeEventListener('mouseup', handleResizeEnd);

            if (finishedResizingTimeoutRef.current) {
                clearTimeout(finishedResizingTimeoutRef.current);
            }

            finishedResizingTimeoutRef.current = setTimeout(() => {
                setJustFinishedResizing(false);
                finishedResizingTimeoutRef.current = null;
            }, 100);
        }
    }, [isResizing, handleResizeMove]);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleResizeMove);
            document.addEventListener('mouseup', handleResizeEnd);
            document.addEventListener('mouseleave', handleResizeEnd);

            // Set cursor based on resize direction
            let cursor = 'default';
            if (resizeDirection === 'bottom-right' || resizeDirection === 'top-left') {
                cursor = 'nw-resize';
            } else if (resizeDirection === 'bottom-left' || resizeDirection === 'top-right') {
                cursor = 'ne-resize';
            } else if (resizeDirection === 'top' || resizeDirection === 'bottom') {
                cursor = 'n-resize';
            } else if (resizeDirection === 'left' || resizeDirection === 'right') {
                cursor = 'e-resize';
            }

            document.body.style.cursor = cursor;
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleResizeMove);
            document.removeEventListener('mouseup', handleResizeEnd);
            document.removeEventListener('mouseleave', handleResizeEnd);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, handleResizeMove, handleResizeEnd, resizeDirection]);

    // Reset dimensions when modal opens
    useEffect(() => {
        if (opened) {
            setModalDimensions({ width: initialWidth, height: initialHeight });
            setIsMaximized(false);
        }
    }, [opened, initialWidth, initialHeight]);

    const toggleMaximize = useCallback(() => {
        if (isMaximized) {
            setModalDimensions({ width: initialWidth, height: initialHeight });
            setIsMaximized(false);
        } else {
            setIsMaximized(true);
            setModalDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        }
    }, [isMaximized, initialWidth, initialHeight]);

    useEffect(() => {
        const handleWindowResize = () => {
            if (isMaximized) {
                setModalDimensions({
                    width: window.innerWidth,
                    height: window.innerHeight
                });
            }
        };

        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [isMaximized]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && opened) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [opened, onClose]);

    useEffect(() => {
        return () => {
            if (finishedResizingTimeoutRef.current) {
                clearTimeout(finishedResizingTimeoutRef.current);
                finishedResizingTimeoutRef.current = null;
            }
        };
    }, []);

    const handleOverlayClick = useCallback((e: React.MouseEvent) => {
        if (isResizing || justFinishedResizing) return;

        if (modalRef.current) {
            const rect = modalRef.current.getBoundingClientRect();
            const bufferZone = 20;
            const clickX = e.clientX;
            const clickY = e.clientY;

            const withinBufferZone = (
                clickX >= rect.left - bufferZone &&
                clickX <= rect.right + bufferZone &&
                clickY >= rect.top - bufferZone &&
                clickY <= rect.bottom + bufferZone
            );

            if (!withinBufferZone) {
                onClose();
            }
        } else {
            onClose();
        }
    }, [isResizing, justFinishedResizing, onClose]);

    if (!opened) return null;

    const modalContent = (
        <div className={`custom-resizeable-modal-overlay ${isResizing ? 'resizing' : ''}`} onClick={handleOverlayClick}>
            <div
                ref={modalRef}
                className={`custom-resizeable-modal-content ${isMaximized ? 'maximized' : ''}`}
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: `${modalDimensions.width}px`,
                    height: `${modalDimensions.height}px`,
                    position: isMaximized ? 'fixed' : 'absolute',
                    top: isMaximized ? 0 : '50%',
                    left: isMaximized ? 0 : '50%',
                    transform: isMaximized ? 'none' : 'translate(-50%, -50%)',
                    right: isMaximized ? 0 : 'auto',
                    bottom: isMaximized ? 0 : 'auto',
                }}
            >
                {/* Resize handles for borders and corners */}
                {!isMaximized && (
                    <>
                        <div
                            className="resize-handle corner top-left"
                            onMouseDown={(e) => handleResizeStart(e, 'top-left')}
                        />
                        <div
                            className="resize-handle corner top-right"
                            onMouseDown={(e) => handleResizeStart(e, 'top-right')}
                        />
                        <div
                            className="resize-handle corner bottom-left"
                            onMouseDown={(e) => handleResizeStart(e, 'bottom-left')}
                        />
                        <div
                            className="resize-handle corner bottom-right"
                            onMouseDown={(e) => handleResizeStart(e, 'bottom-right')}
                        />
                        <div
                            className="resize-handle edge top"
                            onMouseDown={(e) => handleResizeStart(e, 'top')}
                        />
                        <div
                            className="resize-handle edge bottom"
                            onMouseDown={(e) => handleResizeStart(e, 'bottom')}
                        />
                        <div
                            className="resize-handle edge left"
                            onMouseDown={(e) => handleResizeStart(e, 'left')}
                        />
                        <div
                            className="resize-handle edge right"
                            onMouseDown={(e) => handleResizeStart(e, 'right')}
                        />
                    </>
                )}

                <div className="custom-resizeable-modal-wrapper">
                    {title && (
                        <div className="custom-resizeable-modal-header">
                            <Group w='100%' position='apart'>
                                <h4 className="custom-resizeable-modal-title">{title}</h4>
                                <Group spacing='xs'>
                                    <button
                                        onClick={toggleMaximize}
                                        className="custom-resizeable-modal-control custom-maximize-control"
                                        title={isMaximized ? "Restore window" : "Maximize window"}
                                    >
                                        <IonIcon
                                            name={isMaximized ? "contract-outline" : "expand-outline"}
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                paddingTop: '2px'
                                            }}
                                        />
                                    </button>
                                    <button
                                        onClick={onClose}
                                        className="custom-resizeable-modal-control custom-close-control"
                                        title="Close"
                                    >
                                        <IonIcon
                                            name="close-outline"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        />
                                    </button>
                                </Group>
                            </Group>
                        </div>
                    )}
                    <div className="custom-resizeable-modal-body">
                        {typeof children === 'function'
                            ? children({ onMaximize: toggleMaximize, isMaximized })
                            : children
                        }
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
}